import { Anchor, Badge, Divider, Grid, Group, ScrollArea, Stack, Text } from '@mantine/core';
import {
  IconAddressBook,
  IconAlertTriangle,
  IconBell,
  IconBellRinging,
  IconBraces,
  IconBrandWhatsapp,
  IconBuilding,
  IconCircleCheck,
  IconDeviceMobile,
  IconMail,
  IconMailOff,
  IconPhone,
  IconQuestionMark,
  IconSend,
  IconSparkles,
  IconTag,
  IconUserCheck,
  IconUserExclamation,
  IconUserMinus,
  IconUsers,
} from '@tabler/icons-react';
import { JsonTree } from '../../components/JsonTree';
import prettifiedLabel from '../../constants/labelPrettifier';
import ProfileDetailsInsights from './ProfilesDetailsInsights';
import { DASH, formatDateTime, formatDay, InfoRow, Panel } from './ProfilesDetailsUi';

// The profile-level opt-in gates every messaging channel except web and app push:
// those are per-device opt-ins, and the ad audiences carry the platform's own
// subscription state. Mirrors the `marketing`/`optOut` campaign scopes in
// magellan-api (helpers/campaign-profile-loader.js), which skip the opt-in check
// only for pushNotifications, facebookAds and googleAds.
export const MARKETING_CHANNELS = 'Email · SMS · Viber · WhatsApp · Webhooks';

export const marketingSubscription = (profile) => {
  if (profile.unsubscribedAt) {
    return { label: 'Unsubscribed', color: 'red', icon: IconUserExclamation, at: profile.unsubscribedAt };
  }
  if (profile.subscribedAt) {
    return { label: 'Subscribed', color: 'green', icon: IconUserCheck, at: profile.subscribedAt };
  }
  return { label: 'Not subscribed', color: 'gray', icon: IconUserMinus, at: null };
};

const pushSubscription = (subscribedAt, icon) =>
  subscribedAt
    ? { label: 'Subscribed', color: 'green', icon, at: subscribedAt }
    : { label: 'Not subscribed', color: 'gray', icon: IconUserMinus, at: null };

const DELIVERY_CHANNELS = [
  { prefix: 'email', label: 'Email', icon: IconMail },
  { prefix: 'whatsappMessage', label: 'WhatsApp', icon: IconBrandWhatsapp },
  { prefix: 'viberMessage', label: 'Viber', icon: IconPhone },
  { prefix: 'textMessage', label: 'SMS', icon: IconDeviceMobile },
];

// Longest suffix first: `emailSoftBounceAt` has to match `SoftBounceAt` before the
// bare `BounceAt` that `textMessageBounceAt` uses.
const DELIVERY_ISSUES = [
  { suffix: 'SoftBounceAt', label: 'Soft bounce', color: 'yellow' },
  { suffix: 'HardBounceAt', label: 'Hard bounce', color: 'red' },
  { suffix: 'BounceAt', label: 'Bounce', color: 'red' },
  { suffix: 'ComplaintAt', label: 'Complaint', color: 'orange' },
];

// Bounces and complaints arrive as one `<channel><Type>At` field per combination, so
// they are discovered by scanning the profile rather than from a fixed list — a
// channel the API adds later shows up without a change here.
export const collectDeliveryIssues = (profile) =>
  Object.keys(profile)
    .filter((key) => profile[key] && (key.endsWith('BounceAt') || key.endsWith('ComplaintAt')))
    .map((key) => {
      const issue = DELIVERY_ISSUES.find(({ suffix }) => key.endsWith(suffix));
      const channelKey = issue ? key.slice(0, -issue.suffix.length) : key.replace(/At$/, '');
      const channel = DELIVERY_CHANNELS.find(({ prefix }) => prefix === channelKey);
      return {
        key,
        at: profile[key],
        label: issue ? issue.label : prettifiedLabel(channelKey),
        color: issue ? issue.color : 'red',
        channel: channel ? channel.label : prettifiedLabel(channelKey),
        icon: channel ? channel.icon : IconQuestionMark,
      };
    });

// Everything the panels above already spell out is stripped from the raw tree, so it
// holds only what has no dedicated place: profile id, group, custom attributes,
// device ids and the unsubscribe link.
const PANEL_FIELDS = [
  'appPushPlatforms',
  'appPushSubscribedAt',
  'birthday',
  'consent',
  'email',
  'firstName',
  'lastName',
  'phoneNumber',
  'registeredAt',
  'subscribedAt',
  'subscriptions',
  'tags',
  'unsubscribedAt',
  'webPushSubscribedAt',
];

export const rawProfileData = (profile) =>
  JSON.parse(
    JSON.stringify(
      Object.fromEntries(
        Object.entries(profile).filter(
          ([key]) => !PANEL_FIELDS.includes(key) && !key.endsWith('BounceAt') && !key.endsWith('ComplaintAt')
        )
      )
    )
  );

// One channel and its opt-in state. `scope` is the important part: it says which
// channels the state actually covers, which is what makes the profile-level opt-in
// distinguishable from the two per-device push opt-ins.
const ChannelStatus = ({ icon: Icon, title, scope, status, children }) => (
  <div>
    <Group justify="space-between" wrap="nowrap" gap="xs">
      <Group gap={6} wrap="nowrap">
        <Icon size={16} />
        <Text size="sm" fw={500}>
          {title}
        </Text>
      </Group>
      <Badge size="sm" variant="light" color={status.color} leftSection={<status.icon size={12} />}>
        {status.label}
      </Badge>
    </Group>
    <Text size="xs" c="dimmed">
      {scope}
    </Text>
    {status.at && (
      <Text size="xs" c="dimmed">
        since {formatDateTime(status.at)}
      </Text>
    )}
    {children}
  </div>
);

const ProfilesDetailsOverview = ({
  profile,
  companies,
  segmentMemberships,
  loyaltyPoints,
  accountId,
  domainId,
  currency,
}) => {
  const subscriptions = (profile.subscriptions || []).filter(Boolean);
  const tags = (profile.tags || []).filter(Boolean);
  const platforms = profile.appPushPlatforms || [];
  const segments = segmentMemberships || [];
  const memberships = companies || [];
  const deliveryIssues = collectDeliveryIssues(profile);
  const marketing = marketingSubscription(profile);

  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
        <Panel icon={IconAddressBook} title="Contact & identity">
          <Stack gap={6}>
            <InfoRow label="Email">
              {profile.email ? (
                <Anchor href={`mailto:${profile.email}`} size="sm">
                  {profile.email}
                </Anchor>
              ) : (
                DASH
              )}
            </InfoRow>
            <InfoRow label="Phone">
              {profile.phoneNumber ? (
                <Anchor href={`tel:${profile.phoneNumber}`} size="sm">
                  {profile.phoneNumber}
                </Anchor>
              ) : (
                DASH
              )}
            </InfoRow>
            <InfoRow label="Birthday">{formatDay(profile.birthday) || DASH}</InfoRow>
            <InfoRow label="Consent">{profile.consent ? prettifiedLabel(profile.consent) : DASH}</InfoRow>
            <InfoRow label="Registered">{formatDateTime(profile.registeredAt) || 'Not registered'}</InfoRow>
          </Stack>
        </Panel>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
        <Panel icon={IconBellRinging} title="Subscription status">
          <Stack gap="xs">
            <ChannelStatus icon={IconSend} title="Marketing messages" scope={MARKETING_CHANNELS} status={marketing} />
            <Divider />
            <ChannelStatus
              icon={IconBell}
              title="Web push"
              scope="Per browser — separate from the marketing opt-in"
              status={pushSubscription(profile.webPushSubscribedAt, IconBell)}
            />
            <Divider />
            <ChannelStatus
              icon={IconDeviceMobile}
              title="App push"
              scope="Per device — separate from the marketing opt-in"
              status={pushSubscription(profile.appPushSubscribedAt, IconDeviceMobile)}
            >
              {platforms.length > 0 && (
                <Group gap={6} mt={6}>
                  {platforms.map((platform) => (
                    <Badge key={platform} size="sm" variant="default">
                      {platform}
                    </Badge>
                  ))}
                </Group>
              )}
            </ChannelStatus>
          </Stack>
        </Panel>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
        <Panel icon={IconUsers} title="Audience & membership">
          <Stack gap={6}>
            <InfoRow label="Subscriptions" hint="They apply on every channel">
              {subscriptions.length > 0 ? (
                <Group gap={6} justify="flex-end">
                  {subscriptions.map((subscription) => (
                    // Deliberately no channel icon here — a subscription is a topic
                    // opt-in that applies to every channel, not an email-only flag.
                    // tt="none": the API matches these case-sensitively, so show them verbatim.
                    <Badge key={subscription} size="sm" variant="light" tt="none">
                      {subscription}
                    </Badge>
                  ))}
                </Group>
              ) : (
                DASH
              )}
            </InfoRow>
            <InfoRow label="Tags">
              {tags.length > 0 ? (
                <Group gap={6} justify="flex-end">
                  {tags.map((tag) => (
                    // tt="none": the API matches tags case-sensitively, so show them verbatim.
                    <Badge key={tag} size="sm" variant="default" tt="none" leftSection={<IconTag size={12} />}>
                      {tag}
                    </Badge>
                  ))}
                </Group>
              ) : (
                DASH
              )}
            </InfoRow>
            <InfoRow label="Segments">
              {segments.length > 0 ? (
                <Group gap={6} justify="flex-end">
                  {segments.map((segment) => (
                    <Anchor
                      key={segment.id}
                      size="sm"
                      href={`#/accounts/${accountId}/domains/${domainId}/segments/${segment.id}`}
                    >
                      {segment.name}
                    </Anchor>
                  ))}
                </Group>
              ) : (
                DASH
              )}
            </InfoRow>
            <InfoRow label="Companies">
              {memberships.length > 0 ? (
                <Group gap={6} justify="flex-end">
                  {memberships.map((company) => (
                    <Badge
                      key={company.companyId}
                      size="sm"
                      variant="default"
                      component="a"
                      href={`#/accounts/${accountId}/domains/${domainId}/companies/${company.companyId}`}
                      style={{ cursor: 'pointer' }}
                      leftSection={<IconBuilding size={12} />}
                    >
                      {company.name}
                    </Badge>
                  ))}
                </Group>
              ) : (
                DASH
              )}
            </InfoRow>
            <InfoRow label="Loyalty points">{loyaltyPoints ?? 0}</InfoRow>
          </Stack>
        </Panel>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
        <Panel icon={IconMailOff} title="Deliverability">
          {deliveryIssues.length === 0 ? (
            <Group gap={6} wrap="nowrap">
              <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />
              <Text size="sm" c="dimmed">
                No bounces or complaints recorded
              </Text>
            </Group>
          ) : (
            <Stack gap="xs">
              {deliveryIssues.map((issue) => (
                <Group key={issue.key} justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  <Group gap={6} wrap="nowrap">
                    <issue.icon size={16} />
                    <Text size="sm">{issue.channel}</Text>
                  </Group>
                  <div style={{ textAlign: 'right' }}>
                    <Badge size="sm" variant="light" color={issue.color} leftSection={<IconAlertTriangle size={12} />}>
                      {issue.label}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {formatDay(issue.at)}
                    </Text>
                  </div>
                </Group>
              ))}
            </Stack>
          )}
        </Panel>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 8 }}>
        <Panel icon={IconSparkles} title="Customer insights">
          <ProfileDetailsInsights profileInsights={profile.profileInsights} currency={currency} />
        </Panel>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Panel icon={IconBraces} title="Raw profile data">
          <ScrollArea.Autosize mah={360} type="auto" offsetScrollbars>
            <JsonTree defaultExpanded maxDepth={3} data={rawProfileData(profile)} />
          </ScrollArea.Autosize>
        </Panel>
      </Grid.Col>
    </Grid>
  );
};

export default ProfilesDetailsOverview;
